import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Building2, HeartHandshake, CheckCircle2, Clock, Info } from 'lucide-react';
import { toast } from 'sonner';

const STATUS = {
  submitted: { label: 'With the school', cls: 'bg-violet-50 text-violet-600' },
  invited: { label: 'Company invited', cls: 'bg-blue-50 text-blue-600' },
  confirmed: { label: 'Confirmed by the company', cls: 'bg-sky-50 text-sky-700' },
  approved: { label: 'Approved & open to students', cls: 'bg-emerald-50 text-emerald-600' },
  rejected: { label: 'Not taken forward', cls: 'bg-gray-100 text-gray-500' },
  changes_requested: { label: 'School asked for more', cls: 'bg-amber-50 text-amber-700' },
};
const GRADES = [9, 10, 11, 12];
const FORMATS = [{ value: 'on_site', label: 'On-site' }, { value: 'remote', label: 'Remote' }, { value: 'hybrid', label: 'Hybrid' }];
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/** The school's call for internship offers, plus the parent's own proposals. */
export default function ReferralSection({ programs, refreshKey }) {
  const [referrals, setReferrals] = useState([]);
  const [proposing, setProposing] = useState(false);
  const openPrograms = programs.filter(p => p.applications_open !== false);

  const load = async () => {
    try { const r = await api.get('/parent/internship-referrals'); setReferrals(r.data || []); }
    catch { setReferrals([]); }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-6" data-testid="referral-section">
      <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="flex items-center gap-2">
              <HeartHandshake className="h-4 w-4 text-[#F97D6A]" />
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">Call for internship offers</p>
            </div>
            <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mt-1.5">
              Know a company that could host students?
            </h3>
            <p className="text-sm text-[#6B7280] mt-1 leading-relaxed">
              If you work at, run or are connected to an organisation that could host a short internship, propose it
              here. The school invites the company's representative to confirm the details — your referral stays
              credited to you.
            </p>
          </div>
          <Button onClick={() => setProposing(true)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold shrink-0" data-testid="propose-internship-btn">
            <Building2 className="h-4 w-4 mr-2" />Propose an internship
          </Button>
        </div>

        {referrals.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#F3F4F6] space-y-2" data-testid="your-referrals">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Your proposals</p>
            {referrals.map(r => {
              const st = STATUS[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-500' };
              return (
                <div key={r.id} className="rounded-xl bg-[#FAFBFC] px-3.5 py-2.5" data-testid={`referral-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#1F2937]">{r.company_name}</p>
                    <Badge className={`border-0 text-[10px] ${st.cls}`} data-testid={`referral-status-${r.id}`}>{st.label}</Badge>
                  </div>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">
                    {r.title} · {r.program_name || 'No programme yet'} · proposed {fmt(r.created_at?.slice(0, 10))}
                  </p>
                  <p className="text-[11px] text-[#9CA3AF] mt-1 flex items-start gap-1.5 leading-relaxed">
                    {r.status === 'approved' ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0 mt-px" />
                      : <Clock className="h-3 w-3 shrink-0 mt-px" />}
                    {r.status === 'submitted' && 'The school is reviewing your proposal.'}
                    {r.status === 'invited' && `${r.rep_name} has been invited to confirm the internship.`}
                    {r.status === 'confirmed' && 'The company has confirmed. The school is approving the final offer.'}
                    {r.status === 'approved' && 'Families can now see and apply to this internship.'}
                    {r.status === 'rejected' && 'The school decided not to take this forward this round.'}
                    {r.status === 'changes_requested' && 'The school would like a little more detail — propose it again with the extra information.'}
                  </p>
                  {r.coordinator_note && (
                    <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">School note: “{r.coordinator_note}”</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {proposing && (
        <ProposeDialog
          programs={openPrograms}
          onClose={() => setProposing(false)}
          onDone={() => { setProposing(false); load(); }}
        />
      )}
    </div>
  );
}

const EMPTY = {
  program_id: '', company_name: '', industry: '', website: '', address: '', registration_number: '',
  rep_name: '', rep_email: '', relationship: '', title: '', description: '', student_activities: '',
  eligible_grades: [], positions: '', start_date: '', end_date: '', format: '', workplace_address: '',
  notes: '',
};

function ProposeDialog({ programs, onClose, onDone }) {
  const [form, setForm] = useState({ ...EMPTY, program_id: programs[0]?.id || 'none' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const program = programs.find(p => p.id === form.program_id);
  const inputCls = 'mt-1.5 rounded-xl h-11 border-gray-200';

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/parent/internship-referrals', {
        ...form,
        program_id: form.program_id === 'none' ? null : form.program_id,
        positions: form.positions === '' ? null : Number(form.positions),
      });
      toast.success('Proposal sent to the school');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send this proposal'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="propose-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Propose an internship</DialogTitle>
          <DialogDescription className="text-xs">
            Every internship must be hosted by an organisation — the school will contact the representative you name
            to confirm the details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Programme</Label>
            <Select value={form.program_id} onValueChange={v => set('program_id', v)}>
              <SelectTrigger className={inputCls} data-testid="referral-program"><SelectValue placeholder="Choose the programme" /></SelectTrigger>
              <SelectContent>
                {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                <SelectItem value="none">Not for a specific programme</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#9CA3AF] mt-1.5">
              Not sure which round it suits? Leave it general — the school will place it in the right programme.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Company or organisation *</Label>
              <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} className={inputCls} data-testid="referral-company" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Industry</Label>
              <Input value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Architecture" className={inputCls} data-testid="referral-industry" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Website</Label>
              <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" className={inputCls} data-testid="referral-website" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Registration number</Label>
              <Input value={form.registration_number} onChange={e => set('registration_number', e.target.value)} placeholder="If you know it" className={inputCls} data-testid="referral-registration" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Representative's name *</Label>
              <Input value={form.rep_name} onChange={e => set('rep_name', e.target.value)} className={inputCls} data-testid="referral-rep-name" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Representative's email *</Label>
              <Input type="email" value={form.rep_email} onChange={e => set('rep_email', e.target.value)} className={inputCls} data-testid="referral-rep-email" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Business address</Label>
            <Textarea value={form.address} onChange={e => set('address', e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[60px] resize-none" data-testid="referral-address" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Your connection to this organisation</Label>
            <Input value={form.relationship} onChange={e => set('relationship', e.target.value)} placeholder="e.g. I head the studio / I work in their design team" className={inputCls} data-testid="referral-relationship" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Internship title *</Label>
              <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. A week in a design studio" className={inputCls} data-testid="referral-title" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Places available</Label>
              <Input type="number" min="1" value={form.positions} onChange={e => set('positions', e.target.value)} className={inputCls} data-testid="referral-positions" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Starts</Label>
              <Input type="date" min={program?.start_date} max={program?.end_date} value={form.start_date} onChange={e => set('start_date', e.target.value)} className={inputCls} data-testid="referral-start" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Ends</Label>
              <Input type="date" min={form.start_date || program?.start_date} max={program?.end_date} value={form.end_date} onChange={e => set('end_date', e.target.value)} className={inputCls} data-testid="referral-end" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Format</Label>
              <Select value={form.format} onValueChange={v => set('format', v)}>
                <SelectTrigger className={inputCls} data-testid="referral-format"><SelectValue placeholder="On-site, Remote or Hybrid" /></SelectTrigger>
                <SelectContent>{FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Suitable grades</Label>
              <div className="flex gap-2 mt-1.5">
                {GRADES.map(g => {
                  const allowed = !program || program.eligible_grades?.includes(g);
                  const on = form.eligible_grades.includes(g);
                  return (
                    <button key={g} type="button" disabled={!allowed}
                      onClick={() => set('eligible_grades', on ? form.eligible_grades.filter(x => x !== g) : [...form.eligible_grades, g])}
                      className={`h-11 flex-1 rounded-xl text-sm font-semibold border transition-colors ${
                        on ? 'bg-[#FFF0ED] border-[#F97D6A] text-[#F06651]'
                          : allowed ? 'bg-white border-gray-200 text-[#6B7280] hover:border-[#F97D6A]'
                            : 'bg-[#F3F4F6] border-transparent text-[#C8CDD6] cursor-not-allowed'}`}
                      data-testid={`referral-grade-${g}`}>{g}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">What the internship involves</Label>
            <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="A short description the company can refine" className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] resize-none" data-testid="referral-description" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Anything else for the school</Label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[60px] resize-none" data-testid="referral-notes" />
          </div>
          <p className="text-[11px] text-[#9CA3AF] flex items-start gap-1.5 leading-relaxed">
            <Info className="h-3.5 w-3.5 shrink-0 mt-px" />
            Proposing a company does not give you access to applications, resumes or the company's dashboard — the
            representative manages the offer themselves.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.company_name || !form.rep_email || !form.rep_name || !form.title}
            className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="send-referral-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Send to the school
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
